<template>
	<div class="custom-table">
		<div class="table-header">
			<table>
				<thead>
					<tr>
						<th
							class="check"
							v-if="isSelectable"
						>
							<label class="custom-checkbox">
								<input
									type="checkbox"
									:checked="isSelectedAll"
									@change="selectAll($event)"
									:disabled="dataset.length === 0"
								>
								<span class="checkmark"></span>
							</label>
						</th>
						<th
							v-for="field in fields"
							:key="field.name"
							:class="field.name ? field.name.toLowerCase() : ''"
                            :style="field.style"
						>
							{{ field.text || field.name }}
						</th>

						<th
							v-if="!!$scopedSlots['actions']"
							class="actions"
						>
							Action
						</th>
					</tr>
				</thead>
			</table>
		</div>
		<div class="table-data">
			<table>
				<tbody v-if="dataset.length > 0 && !loading">
					<tr
						v-for="item in dataset"
						:key="item[keyField]"
						@click="$emit('click', item)"
					>
						<td
							class="check"
							v-if="isSelectable"
						>
							<label
								class="custom-checkbox"
								@click.stop
							>
								<input
									type="checkbox"
									@input="select($event, item[keyField])"
									:checked="selectedItems.indexOf(item[keyField]) > -1"
								>
								<span class="checkmark"></span>
							</label>
						</td>

						<td
							v-for="field in fields"
							:key="field.name"
							:class="field.name ? field.name.toLowerCase() : ''"
                            :style="field.style"
						>
							<slot
								:props="{ item }"
								:name="field.name"
							>
								{{ item[field.name] || '--'}}
							</slot>
						</td>

						<td
							v-if="!!$scopedSlots['actions']"
							class="actions"
						>
							<slot
								:item="item"
								name="actions"
							>
							</slot>
						</td>
					</tr>
				</tbody>
			</table>

			<div
				class="empty"
				v-if="dataset.length === 0 && !loading"
			>
				<slot name="empty">
					No Result
				</slot>
			</div>

			<div
				class="empty"
				v-if="loading"
			>
				<slot name="loading">
					Loading ...
				</slot>
			</div>
		</div>

		<div
			class="paginate"
			v-if="hasPagination"
		>
			<div class="count-option">
				{{ `${rangeFrom}-${rangeTo} of ${totalCount}` }}

				<div
					v-if="!hidePerPageOption"
					class="options"
					tabindex="0"
					:class="{'active' : isCountOptionVisible}"
					@blur="isCountOptionVisible = false"
					@click="isCountOptionVisible = !isCountOptionVisible"
				>
					<div class="count">{{ itemsPerPage }}</div>

					<ul v-if="isCountOptionVisible">
						<li
							v-for="i in 5"
							:key="i"
							@click="changePage(undefined, i*50/5)"
						>
							{{ i*50/5 }}
						</li>
					</ul>
				</div>
			</div>

			<div class="pages">
				<ul>
					<li
						:class="{'disabled' : currentPage === 1 || loading}"
						@click="changePage(currentPage-1, undefined)"
					>
						Previous
					</li>
					<li
						:class="{
							'disabled' : page==='...' || loading,
							'active' : page === currentPage
						}"
						@click="changePage(page, undefined)"
						v-for="(page, index) in pagination"
						:key="index"
					>
						{{ page }}
					</li>
					<li
						@click="changePage(currentPage+1, undefined)"
						:class="{'disabled' : currentPage === totalPage || loading}"
					>
						Next
					</li>
				</ul>
			</div>
		</div>
	</div>
</template>

<script>

export default {
	props: {
		keyField: {
			type: String,
			default: 'id'
		},

		fields: {
			type: Array,
			default: () => [],
			required: true
		},

		dataset: {
			type: Array,
			default: () => [],
			required: true
		},

		selectedItems: {
			type: Array,
			default: () => []
		},

		loading: {
			type: Boolean,
			default: false
		},

		currentPage: {
			type: Number,
			default: 1
		},

		totalCount: {
			type: Number,
			default: 0
		},

		itemsPerPage: {
			type: Number,
			default: 0
		},

		isSelectable: {
			type: Boolean,
			default: true
		},

		hasPagination: {
			type: Boolean,
			default: true
		},

		hidePerPageOption: {
			type: Boolean,
			default: false
		}
	},

	data () {
		return {
			isCountOptionVisible: false
		}
	},

	computed: {
		isSelectedAll () {
			return this.dataset.length > 0 &&
					this.dataset.every(i => this.selectedItems.indexOf(i[this.keyField]) > -1)
		},

		totalPage () {
			const total = Math.ceil(this.totalCount / this.itemsPerPage)
			return !isNaN(total) ? total : 1
		},

		rangeFrom () {
			if (!this.totalCount) return 0
			return 1 + ((this.currentPage * this.itemsPerPage) - this.itemsPerPage)
		},

		rangeTo () {
			if (!this.totalCount) return 0
			return this.rangeFrom + (this.dataset.length) - 1
		},

		pagination () {
			const currentPage = this.currentPage
			const pageCount = this.totalPage
			const delta = 2
			const start = Math.max(2, currentPage - delta)
			const end = Math.min(pageCount - 1, currentPage + delta)

			const range = []
			for (let i = start; i <= end; i++) {
				range.push(i)
			}

			if (currentPage - delta > 2) {
				range.unshift('...')
			}
			if (currentPage + delta < pageCount - 1) {
				range.push('...')
			}

			range.unshift(1)
			if (pageCount > 1) {
				range.push(pageCount)
			}

			return range
		}
	},

	methods: {
		select (e, item) {
			/* the variable assigned to selectedItems will also be updated by reference */
			const selected = this.selectedItems

			const index = selected.indexOf(item)

			if (e.target.checked) {
				if (index < 0) selected.push(item)
			} else {
				if (index > -1) selected.splice(index, 1)
			}

			this.$emit('select', selected)
		},

		selectAll (e) {
			/* the variable assigned to selectedItems will also be updated by reference */
			const selected = this.selectedItems

			this.dataset.forEach(i => {
				const index = selected.indexOf(i[this.keyField])

				if (e.target.checked) {
					if (index < 0) selected.push(i[this.keyField])
				} else {
					if (index > -1) selected.splice(index, 1)
				}
			})

			this.$emit('select', selected)
		},

		changePage (page = this.currentPage, itemsPerPage = this.itemsPerPage) {
			const paginate = {
				currentPage: page,
				itemsPerPage: itemsPerPage
			}

			this.$emit('changePage', paginate)
		}
	}
}
</script>

<style lang="scss" scoped>
.custom-table {
	height: calc(100% - 3em);
	width: 100%;
	display: flex;
	flex-direction: column;

	.table-header {
		padding-right: 10px;
		background-color: #F8F9FB;
	}

	.table-data {
		flex: 1 1 auto;
		overflow-y: scroll;
		position: relative;
		background-color: #fff;

		table {
            height: auto;
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
		}
	}

	table {
		width: 100%;
		table-layout: fixed;
		border-collapse: collapse;

		thead {
			tr {
				th {
					font-weight: 400;
					vertical-align: top;
				}
			}
		}

		tbody {
						padding: 0 20px;
			tr {

                @media screen
                    and (min-device-width: 1200px)
                    and (-webkit-min-device-pixel-ratio: 1) {
                            td {
                                word-wrap: none;
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                            }

                            &:hover td {
                                word-break: break-word;
                                word-wrap: break-word;
                                white-space: normal;
                            }
                        }
				// &:nth-child(odd){
				// 	background-color: #fff;
				// }

				// &:nth-child(even){
				// 	background-color: #F8F8F8;
				// }

				border-bottom: 1px solid #E6EAF2;

				td {
					overflow-wrap: break-word;
				}
			}
		}

		td, th {
			text-align: left;
			padding: 10px;

			&.check {
				width: 40px;
			}
		}

		th {
			font-size: 1em;
			font-weight: 600;
		}

		td.actions {
			display: flex;
			align-items: center;
		}
	}

	.empty {
		height: 100%;
		display: flex;
		justify-content: center;
		align-items: center;
		font-weight: 600;
		color: #D0D0DF;
		font-size: 1.5em;
    }

    label.custom-checkbox {
        display: inline-block;
        position: relative;
        cursor: pointer;
        height: 18px;
        width: 18px;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
		user-select: none;

        input {
            position: absolute;
            opacity: 0;
            cursor: pointer;
            height: 0;
			width: 0;
        }

        .checkmark {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
			height: 100%;
			border-radius: 3px;
			background-color: #fff;
			border: 1px solid #D0D0DF;
            box-shadow: 0 0 2px #00000020;
            display: flex;
			justify-content: center;
			align-items: center;
            transition: 0.3s;

            &:after {
                content: "";
                display: none;
                width: 4px;
				height: 8px;
				margin-bottom: 2px;
                border: solid #fff;
                border-width: 0 2px 2px 0;
                -webkit-transform: rotate(45deg);
                -ms-transform: rotate(45deg);
                transform: rotate(45deg);
            }
        }

        input:checked ~ .checkmark{
            background-color: #2DC4B6;
            &:after {
                display: block;
            }
        }

        &:hover {
            .checkmark {
                border-color: #2DC4B6;
            }
        }
	}

	.paginate {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 15px 10px;
		color: #555;

		.count-option {
			display: flex;
			align-items: center;
		}

		.options {
			display: inline-block;
			border: 1px solid #D0D0DF;
			border-radius: 5px;
			margin-left: 10px;
			min-width:  50px;
			position: relative;
			cursor: pointer;

			&.active,
			&:focus,
			&.active ul,
			&:focus ul {
				outline: none;
				border-color: #2DC4B6;
			}

			.count {
				text-align: center;
				display: flex;
				align-items: center;
				padding: 5px 10px;

				&:after {
					content: '';
					display: inline-block;
					transform: rotate(-45deg);
					width: 8px;
					height: 8px;
					margin-top: 5px;
					border-width: 2px 2px 0 0;
					border-style: solid;
					border-color: #D0D0DF;
					margin-left: 15px;
				}
			}

			ul {
				position: absolute;
				width: 100%;
				margin-bottom: 5px;
				bottom: 100%;
				left: 0;
				border: 1px solid #D0D0DF;
				background-color: #fff;
				border-radius: 5px;
				max-height: 400px;
				overflow: auto;
				list-style: none;

				li {
					padding: 5px;

					&:hover {
						background-color: #eee;
					}
				}
			}
		}

		.pages {
			ul {
				display: flex;
				align-items: center;
				border: 1px solid #D0D0DF;
				border-radius: 5px;
				box-shadow: 0 0 1px #00000020;
				list-style: none;

				li {
					text-align: center;
					cursor: pointer;
					padding: 5px 10px;

					&:not(:last-child) {
						border-style: solid;
						border-color: #D0D0DF;
						border-width: 0 1px 0 0;
					}

					&.disabled {
						opacity: 0.5;
						pointer-events: none;
					}

					&.active {
						background-color: #18CDBC;
						color: #fff;
					}
				}
			}
		}

	}
}
</style>
